import React, { useEffect, useState } from "react";
import {
  Box, Button, Flex, FormControl, FormLabel, Heading, Input,
  Spacer, Stack, Text, useToast
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../../utils/authFetch";

export default function CustomerManager() {
  const toast = useToast();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const isSystemAdmin = currentUser.role === "system-admin";

  const [customers, setCustomers] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", detail: "" });

  const API = process.env.REACT_APP_API_URL || "";

  const fetchCustomers = async () => {
    try {
      const res = await authFetch(`${API}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({ title: "Error loading customers", status: "error" });
    }
  };

  useEffect(() => {
    if (!isSystemAdmin) return;
    fetchCustomers();
  }, []);

  const resetForm = () => {
    setForm({ name: "", detail: "" });
    setEditingId(null);
    setFormVisible(false);
  };

  const validateForm = () => {
    if (!form.name) {
      toast({ title: "Please enter a customer name.", status: "warning" });
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;
    try {
      const res = await authFetch(`${API}/api/customers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Add failed");
      toast({ title: "Customer added", status: "success" });
      resetForm();
      fetchCustomers();
    } catch {
      toast({ title: "Failed to add customer", status: "error" });
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    try {
      const res = await authFetch(`${API}/api/customers/${editingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Update failed");
      toast({ title: "Customer updated", status: "success" });
      resetForm();
      fetchCustomers();
    } catch {
      toast({ title: "Update failed", status: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this customer?")) return;
    try {
      await authFetch(`${API}/api/customers/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Customer deleted", status: "info" });
      fetchCustomers();
      resetForm();
    } catch {
      toast({ title: "Delete failed", status: "error" });
    }
  };

  if (!isSystemAdmin) {
    return <Box p={8}><Heading size="md">Access Denied : {currentUser.role}</Heading></Box>;
  }

  return (
    <Flex justify="center" mt={10}>
      <Box p={10} w="70%" borderWidth={1} boxShadow={"lg"}>
        <Heading mb={4}>Manage Customers</Heading>

        {!formVisible && (
          <Button colorScheme="iBlue" mb={6} onClick={() => {
            setFormVisible(true);
            setForm({ name: "", detail: "" });
            setEditingId(null);
          }}>Add Customer</Button>
        )}

        {formVisible && (
          <Stack spacing={3} mb={6}>
            <FormControl isRequired>
              <FormLabel>Customer Name</FormLabel>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </FormControl>
            <FormControl>
              <FormLabel>Detail</FormLabel>
              <Input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} />
            </FormControl>
            <Flex gap={4}>
              <Button colorScheme="iTeal" onClick={() => (editingId ? handleUpdate() : handleAdd())}>Save</Button>
              <Button colorScheme="iRed" onClick={resetForm}>Cancel</Button>
              {editingId && (
                <Button colorScheme="iOrange" onClick={() => handleDelete(editingId)}>Delete</Button>
              )}
            </Flex>
          </Stack>
        )}

        <Heading size="md" mb={4}>Customer List</Heading>
        <Stack spacing={3}>
          {customers.map((c) => (
            <Box key={c.id} borderWidth={1} borderRadius="md" p={4}>
              <Flex>
                <Box>
                  <Text fontWeight="bold">{c.name}</Text>
                  <Text fontSize="sm" color="gray.600">{c.detail}</Text>
                </Box>
                <Spacer />
                <Button size="sm" colorScheme="iBlue" onClick={() => {
                  setFormVisible(true);
                  setEditingId(c.id);
                  setForm({ name: c.name, detail: c.detail });
                }}>Edit</Button>
              </Flex>
            </Box>
          ))}
        </Stack>

        <Flex justify="center" mt={8}>
          <Button onClick={() => navigate("/")} colorScheme="gray" variant="outline">Back to Menu</Button>
        </Flex>
      </Box>
    </Flex>
  );
}